import { lazy, Suspense, useEffect, useState } from "react";
import GoldButton from "../ui/GoldButton";
import GhostButton from "../ui/GhostButton";
import BackgroundGradient from "../effects/BackgroundGradient";
import PretextReveal from "../motion/PretextReveal";

// Only load Three.js on desktop (saves ~862KB on mobile)
const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
const HeroScene = isMobile ? null : lazy(() => import("../three/HeroScene"));

export default function Hero() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Trigger hero animations on next paint (no artificial delay)
    requestAnimationFrame(() => setLoaded(true));
  }, []);

  return (
    <>
      <section
        className="owl-hero"
        style={{
          minHeight: "100vh",
          background: "#050505",
          display: "flex",
          alignItems: "center",
          padding: "120px 40px 80px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* 3D Background — skipped entirely on mobile to save ~862KB */}
        {HeroScene && (
          <Suspense fallback={null}>
            <HeroScene />
          </Suspense>
        )}

        {/* Gradient blobs */}
        <BackgroundGradient />

        {/* Scroll indicator */}
        <div
          style={{
            position: "absolute",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            opacity: loaded ? 0.5 : 0,
            transition: "opacity 0.6s ease 1.4s",
            animation: "scrollBounce 2s ease-in-out infinite",
          }}
        >
          <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 10, letterSpacing: "0.2em", color: "#908A83", textTransform: "uppercase" }}>Scroll</span>
          <svg width="16" height="24" viewBox="0 0 16 24" fill="none">
            <path d="M8 4 L8 18 M3 14 L8 19 L13 14" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <div
          className="owl-hero-grid"
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            width: "100%",
            display: "grid",
            gridTemplateColumns: "55% 45%",
            gap: 60,
            alignItems: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 40,
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#D4A843", display: "inline-block", opacity: loaded ? 1 : 0, transition: "opacity 0.4s ease 0.05s" }} />
              <PretextReveal
                mode="slide"
                as="span"
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: "0.3em",
                  color: "#908A83",
                  textTransform: "uppercase" as const,
                }}
                delay={0.05}
              >
                DELHI, INDIA HQ × GLOBAL DELIVERY
              </PretextReveal>
            </div>

            <div style={{ marginBottom: 32 }}>
              <PretextReveal
                mode="scatter"
                as="div"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 300,
                  fontSize: "clamp(64px, 9vw, 108px)",
                  color: "#F8F4EC",
                  letterSpacing: "-0.02em",
                  lineHeight: 0.88,
                }}
                delay={0.1}
                idle="float"
              >
                INTELLIGENT
              </PretextReveal>
              <PretextReveal
                mode="scatter"
                as="div"
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: "clamp(64px, 9vw, 108px)",
                  color: "#D4A843",
                  letterSpacing: "-0.01em",
                  lineHeight: 0.92,
                }}
                delay={0.4}
                idle="float"
              >
                SYSTEMS.
              </PretextReveal>
            </div>

            <PretextReveal
              mode="wave"
              as="p"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 300,
                fontSize: 18,
                color: "#8B8378",
                lineHeight: 1.65,
                maxWidth: 460,
                marginBottom: 28,
              }}
              delay={0.3}
            >
              We design and build websites, custom software, CRM, ERP, AI agents, and automation systems — all under one roof.
            </PretextReveal>

            <div
              style={{
                width: 48,
                height: "0.5px",
                background: "#D4A843",
                marginBottom: 28,
                opacity: loaded ? 1 : 0,
                transition: "opacity 0.5s ease 0.9s",
              }}
            />

            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 20,
                opacity: loaded ? 1 : 0,
                transform: loaded ? "none" : "translateY(12px)",
                transition: "all 0.7s ease 1s",
              }}
            >
              <div className={`btn-gold-fill${loaded ? " cta-pulse-wrap" : ""}`} style={{ display: "inline-block", borderRadius: 4 }}>
                <GoldButton href="/contact" size="md">
                  BEGIN YOUR PROJECT
                </GoldButton>
              </div>
              <GhostButton href="/work">SEE OUR WORK</GhostButton>
            </div>

            <PretextReveal
              mode="cascade"
              as="div"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 300,
                fontSize: 14,
                color: "#908A83",
                letterSpacing: "0.05em",
              }}
              delay={1.2}
            >
              500+ projects · 200+ clients · 15+ industries
            </PretextReveal>

            {/* Mobile-only stat badges — visible only when hero visual is hidden */}
            <div
              className="owl-hero-stats-mobile"
              style={{
                display: "none",
                gap: 12,
                marginTop: 32,
                flexWrap: "wrap",
                opacity: loaded ? 1 : 0,
                transition: "opacity 0.6s ease 1.4s",
              }}
            >
              {[
                { val: "500+", label: "Projects Delivered" },
                { val: "₹50Cr+", label: "Revenue Built" },
                { val: "AI-Powered", label: "24/7 Automation" },
              ].map((s) => (
                <div
                  key={s.val}
                  style={{
                    background: "rgba(14,14,14,0.9)",
                    border: "1px solid rgba(212,168,67,0.25)",
                    borderRadius: 4,
                    padding: "10px 16px",
                  }}
                >
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#D4A843", lineHeight: 1 }}>{s.val}</div>
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: 12, color: "#908A83", marginTop: 3 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="owl-hero-visual" style={{ position: "relative", minHeight: 500 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                animation: "float 4s ease-in-out infinite",
                opacity: loaded ? 1 : 0,
                transition: "opacity 1s ease 0.6s",
              }}
            >
              <img
                src="/owl-monolith.svg"
                alt="OWL"
                style={{
                  height: "clamp(300px, 40vw, 500px)",
                  objectFit: "contain",
                  filter: "drop-shadow(0 0 60px rgba(212,168,67,0.3))",
                }}
              />
            </div>

            <div
              style={{
                position: "absolute",
                top: "5%",
                right: 0,
                background: "rgba(14,14,14,0.9)",
                border: "1px solid rgba(212,168,67,0.25)",
                borderRadius: 4,
                padding: "14px 20px",
                backdropFilter: "blur(12px)",
                opacity: loaded ? 1 : 0,
                transition: "opacity 0.6s ease 1.2s",
              }}
            >
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: "#D4A843", lineHeight: 1 }}>500+</div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: 13, color: "#908A83", marginTop: 4 }}>
                Projects Delivered
              </div>
            </div>

            <div
              style={{
                position: "absolute",
                top: "45%",
                left: 0,
                background: "rgba(14,14,14,0.9)",
                border: "1px solid rgba(212,168,67,0.25)",
                borderRadius: 4,
                padding: "14px 20px",
                backdropFilter: "blur(12px)",
                opacity: loaded ? 1 : 0,
                transition: "opacity 0.6s ease 1.4s",
              }}
            >
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: "#D4A843", lineHeight: 1 }}>₹50Cr+</div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: 13, color: "#908A83", marginTop: 4 }}>
                Revenue Built
              </div>
            </div>

            <div
              style={{
                position: "absolute",
                bottom: "10%",
                right: "5%",
                background: "rgba(14,14,14,0.9)",
                border: "1px solid rgba(212,168,67,0.25)",
                borderRadius: 4,
                padding: "14px 20px",
                backdropFilter: "blur(12px)",
                opacity: loaded ? 1 : 0,
                transition: "opacity 0.6s ease 1.6s",
              }}
            >
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: "#D4A843", lineHeight: 1 }}>AI-Powered</div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: 13, color: "#908A83", marginTop: 4 }}>
                24/7 Automation
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .owl-hero {
            padding: 108px 24px 72px !important;
          }

          .owl-hero-grid {
            grid-template-columns: 1fr !important;
            gap: 0 !important;
          }

          .owl-hero-visual {
            display: none !important;
          }

          .owl-hero-stats-mobile {
            display: flex !important;
          }
        }
      `}</style>
    </>
  );
}
